import React, { useEffect, useRef, useState } from "react";
import { Modal } from "react-bootstrap";
import ModalCloseButton from "components/ModalCloseButton";
import api from "services/api";
import Translator, { __ } from "services/Translator";
import { resendLoginOtp, verifyLoginOtp } from "services/authOtp";
import SignupBonusDropdown from "components/SignupBonusDropdown";
import { filterCuratedCampaigns, isBonusDropdownEnabled } from "utils/signupFlow";

const OTP_LENGTH = 6;
const RESEND_COOLDOWN = 45; // seconds

export default function AuthOtpModal({
  show,
  email,
  onClose,
  onSuccess,
  onError,
  setSpinner,
}) {
  const [otpCode, setOtpCode] = useState("");
  const [otpError, setOtpError] = useState("");
  const [infoMessage, setInfoMessage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [resendIn, setResendIn] = useState(RESEND_COOLDOWN);
  const inputRef = useRef(null);
  const submittedCodeRef = useRef("");

  // Bonus selection lives here (under the code field) for the passwordless flow.
  const [initialSettings, setInitialSettings] = useState({});
  const [campaigns, setCampaigns] = useState([]);
  const [isCampaignsLoading, setIsCampaignsLoading] = useState(true);
  const [bonusOffer, setBonusOffer] = useState("no_bonus");
  const [hasManualBonusSelection, setHasManualBonusSelection] = useState(false);

  const bonusEnabled = isBonusDropdownEnabled(initialSettings);
  const curatedCampaigns = filterCuratedCampaigns(campaigns, initialSettings);

  useEffect(() => {
    if (!show) {
      setOtpCode("");
      setOtpError("");
      setInfoMessage("");
      setIsSubmitting(false);
      setIsResending(false);
      setBonusOffer("no_bonus");
      setHasManualBonusSelection(false);
      setResendIn(RESEND_COOLDOWN);
      submittedCodeRef.current = "";
      return;
    }

    setInfoMessage(__("A verification code has been sent to your email. Please enter it below."));
    setResendIn(RESEND_COOLDOWN);
    // Autofocus the code field at the highest drop-off step.
    const focusTimer = setTimeout(() => inputRef.current?.focus(), 120);

    // Fetch the brand config + active campaigns so the bonus selector can render
    // under the code field. Best-effort: failure just hides the selector.
    let cancelled = false;
    (async () => {
      try {
        const settingsRes = await api.get("/initial-settings");
        if (!cancelled && settingsRes.status === 200 && settingsRes.data) {
          setInitialSettings(settingsRes.data);
        }
      } catch (e) {
        /* ignore */
      }
      try {
        setIsCampaignsLoading(true);
        const campRes = await api.post("/get-registration-campaigns", {
          language: Translator.getSelectedLanguage()?.code,
          currency: "USD",
        });
        if (!cancelled && campRes.status === 200) {
          setCampaigns(Array.isArray(campRes.data) ? campRes.data : []);
        }
      } finally {
        if (!cancelled) setIsCampaignsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
      clearTimeout(focusTimer);
    };
  }, [show, email]);

  // Pre-select the first curated campaign once loaded (unless the user picked).
  useEffect(() => {
    if (hasManualBonusSelection || isCampaignsLoading) return;
    if (!bonusEnabled || !curatedCampaigns.length) {
      setBonusOffer("no_bonus");
      return;
    }
    setBonusOffer(curatedCampaigns[0].id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaigns, initialSettings, hasManualBonusSelection, isCampaignsLoading]);

  const handleBonusSelection = (value) => {
    setBonusOffer(value);
    setHasManualBonusSelection(true);
  };

  // Resend cooldown ticker.
  useEffect(() => {
    if (!show || resendIn <= 0) return;
    const t = setInterval(() => {
      setResendIn((prev) => (prev <= 1 ? 0 : prev - 1));
    }, 1000);
    return () => clearInterval(t);
  }, [show, resendIn]);

  const verify = async (code) => {
    if (isSubmitting || submittedCodeRef.current === code) return;
    submittedCodeRef.current = code;

    setIsSubmitting(true);
    setSpinner?.(true);
    setOtpError("");

    try {
      const response = await verifyLoginOtp(email, code, bonusEnabled ? bonusOffer : undefined);
      if (response.status === 200) {
        await onSuccess?.(response.data);
        return;
      }
      // Ordinary invalid-code responses stay inline under the field only —
      // no global alert (avoids the same error showing twice).
      setOtpError(response.data?.message || __("Invalid verification code"));
      submittedCodeRef.current = "";
    } catch (err) {
      // Unexpected transport failure — surface via the global alert too.
      const message = err?.message || __("Unable to verify code. Please try again.");
      setOtpError(message);
      onError?.(message);
      submittedCodeRef.current = "";
    } finally {
      setIsSubmitting(false);
      setSpinner?.(false);
    }
  };

  const handleOtpChange = (ev) => {
    const value = ev.target.value.replace(/\D/g, "").slice(0, OTP_LENGTH);
    setOtpCode(value);
    setOtpError("");
    if (value.length === OTP_LENGTH) {
      verify(value); // auto-submit once the full code is entered
    }
  };

  const handleSubmit = (ev) => {
    ev.preventDefault();
    const code = String(otpCode || "").trim();
    if (code.length !== OTP_LENGTH) {
      setOtpError(__("Please enter the 6-digit code sent to your email"));
      return;
    }
    verify(code);
  };

  const handleResend = async () => {
    if (resendIn > 0 || isResending) return;
    setIsResending(true);
    setOtpError("");
    setSpinner?.(true);

    try {
      const response = await resendLoginOtp(email);
      if (response.status === 200) {
        setInfoMessage(__("A new verification code has been sent to your email."));
        setResendIn(RESEND_COOLDOWN);
      } else {
        setOtpError(response.data?.message || __("Unable to resend verification code"));
      }
    } catch (err) {
      setOtpError(err?.message || __("Unable to resend verification code"));
    } finally {
      setIsResending(false);
      setSpinner?.(false);
    }
  };

  return (
    <Modal
      show={show}
      onHide={onClose}
      centered
      backdropClassName="loginRegModal-backdrop--blur"
      className="loginRegModal loginRegModal--streamlined auth-otp-modal"
    >
      <Modal.Body className="login_modal login_modal--streamlined">
        <div className="betmode-auth">
          <section className="betmode-auth__panel betmode-auth__panel--form auth-otp-modal__panel">
            <ModalCloseButton onClick={onClose} className="betmode-auth__close modal_close_reg" />

            <div className="betmode-auth__brand">
              <img
                alt="Play365"
                className="betmode-auth__logo"
                src={require("assets/play365/images/logos/logo.webp")}
              />
            </div>

            <h2 className="betmode-auth__title">{__("Verify your email")}</h2>

            <p className="auth-otp-modal__message" aria-live="polite">{infoMessage}</p>
            <p className="auth-otp-modal__email">{email}</p>

            <form className="betmode-auth__form" autoComplete="off" onSubmit={handleSubmit}>
              <div className="betmode-auth__field">
                <label className="betmode-auth__label" htmlFor="auth-otp-code">
                  {__("Enter the 6-digit code")}
                </label>
                <input
                  id="auth-otp-code"
                  ref={inputRef}
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={OTP_LENGTH}
                  name="otp_code"
                  value={otpCode}
                  onChange={handleOtpChange}
                  className={"betmode-auth__input auth-otp-modal__input " + (otpError ? "betmode-auth__input--error" : "")}
                  placeholder={"— — — — — —"}
                  aria-describedby="auth-otp-error"
                />
                <div className="betmode-auth__error" id="auth-otp-error" role="alert">{otpError}</div>
              </div>

              {bonusEnabled ? (
                <div className="betmode-auth__field betmode-auth__field--bonus">
                  <label className="betmode-auth__label">{__("Choose your bonus")}</label>
                  <SignupBonusDropdown
                    campaigns={curatedCampaigns}
                    isCampaignsLoading={isCampaignsLoading}
                    bonusOffer={bonusOffer}
                    onSelectBonus={handleBonusSelection}
                  />
                </div>
              ) : null}

              <button
                type="submit"
                className="betmode-auth__submit"
                disabled={isSubmitting || otpCode.length !== OTP_LENGTH}
              >
                {isSubmitting ? __("Verifying…") : __("Verify & continue")}
              </button>
            </form>

            <button
              type="button"
              className="betmode-auth__back-link auth-otp-modal__resend"
              onClick={handleResend}
              disabled={isResending || resendIn > 0}
            >
              {resendIn > 0
                ? __("Resend code in") + " " + resendIn + "s"
                : __("Resend code")}
            </button>

            <button type="button" className="betmode-auth__back-link" onClick={onClose}>
              {__("Use a different email")}
            </button>
          </section>
        </div>
      </Modal.Body>
    </Modal>
  );
}
